<template>
  <v-card class="p-3">
    <v-card-title>Filter</v-card-title>
    <v-card-text>
      <v-row align="end">
        <v-col cols="4">
          <date-picker v-model="fromDateVal" :initial-value="defaultFromDt" label="Acquired Date From"/>
        </v-col>
        <v-col cols="4">
          <date-picker v-model="thruDateVal" :initial-value="defaultToDt" label="Acquired Date Thru"/>
        </v-col>
        <v-col cols="4">
          <v-checkbox
              v-model="onlyUntagged"
              :label="'Only show untagged images'"
          ></v-checkbox>
          <v-checkbox
              v-model="includePartiallyTagged"
              :label="'Include partially tagged'"
              :disabled="!onlyUntagged"
          ></v-checkbox>
          <v-checkbox
              v-model="includeInactive"
              :label="'Include inactive photos'"
          ></v-checkbox>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="4">
          <v-autocomplete :items="allCats"
                          v-model="selectedCategory"
                          :value="allCats[0].id"
                          class="mt-4" item-text="name" item-value="id"
                          label="Tag Category" />
        </v-col>

        <v-col cols="8">
          <v-autocomplete v-model="selectedTags"
                          :filter="tagFilter"
                          :items="filteredTags"
                          :search-input.sync="tagSearchInput"
                          chips
                          class="mt-4"
                          label="Tags"
                          multiple
                          small-chips
                          @change="tagSearchInput=''"
          >
            <template v-slot:selection="data">
              <v-chip
                  v-bind="data.attrs"
                  :input-value="data.selected"
                  close
                  @click="data.select"
                  @click:close="removeTagFromSelected(data.item)"
              >
                {{ data.item.name }}
              </v-chip>
            </template>
            <template v-slot:item="data">
              <v-list-item-content>
                <v-list-item-title v-html="data.item.name"></v-list-item-title>
                <v-list-item-subtitle v-if="data.item.category"
                                      v-html="data.item.category.name"></v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-autocomplete>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="6">
          <v-autocomplete v-model="selectedProjects"
                          :filter="projectFilter"
                          :items="getProjects"
                          chips
                          class="mt-4"
                          label="Projects"
                          multiple
                          small-chips
          >
            <template v-slot:selection="data">
              <v-chip
                  v-bind="data.attrs"
                  :input-value="data.selected"
                  close
                  @click="data.select"
                  @click:close="removeProjectFromSelected( data.item)"
              >
                <div style="overflow: hidden">{{ data.item.id }} - {{ data.item.name }}</div>
              </v-chip>
            </template>
            <template v-slot:item="data">
              <v-list-item-content>
                <v-list-item-title v-html="`${data.item.id} - ${data.item.name}`"></v-list-item-title>
              </v-list-item-content>
            </template>

          </v-autocomplete>
        </v-col>

        <v-col cols="6">
          <v-autocomplete v-model="selectedJobs"
                          :items="getJobs"
                          :filter="jobFilter"
                          chips
                          class="mt-4"
                          label="Jobs"
                          multiple
                          small-chips
          >
            <template v-slot:selection="data">
              <v-chip
                  v-bind="data.attrs"
                  :input-value="data.selected"
                  close
                  @click="data.select"
                  @click:close="removeJobFromSelected( data.item)"
              >
                <div style="overflow: hidden">{{ data.item.id }} - {{ data.item.name }}</div>
              </v-chip>
            </template>
            <template v-slot:item="data">
              <v-list-item-content>
                <v-list-item-title v-html="`${data.item.id} - ${data.item.name}`"></v-list-item-title>
              </v-list-item-content>
            </template>

          </v-autocomplete>
        </v-col>


        <v-col cols="6">
          <v-text-field
              v-model="respIdSearchFreeForm"
              label="Mass Response ID Search"
              hint="Photo response_id values. Space- or comma-separated."
              persistent-hint
          >
          </v-text-field>
        </v-col>

        <v-col cols="6">
          <v-text-field
              v-model="rgidSearchFreeForm"
              label="Response Group ID Search"
              hint="Store-visit RGIDs (photo.response_group_id). Space- or comma-separated."
              persistent-hint
          >
          </v-text-field>
        </v-col>

        <v-col cols="6">
          <v-text-field
              v-model="question_id"
              label="Question ID"
              hint="Find photos for a specific question_id."
              persistent-hint
          >
          </v-text-field>
        </v-col>

      </v-row>

      <v-row>

        <v-col cols='4'>

            <v-select v-model='selectedRetailer'
              label='Retailer'
              :items='retailerChoices'
              :loading='$store.state.loading_list.includes("get_retailer_list")'
              clearable />

        </v-col>

        <v-col cols='4'>

            <v-select v-model='selectedDepartment'
              label='Department'
              :items='departmentChoices'
              :loading='$store.state.loading_list.includes("get_department_list")'
              clearable />

        </v-col>

        <v-col cols='4'>

            <v-autocomplete
              v-model='selectedCountry'
              :items='countries'
              item-text='label'
              item-value='value'
              label='Country'
              clearable />

        </v-col>

      </v-row>

    </v-card-text>

    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn color="error" text @click="cancelClicked()">
        Cancel
      </v-btn>
      <v-btn color="success" text @click="okClicked()">
        Ok
      </v-btn>
    </v-card-actions>

  </v-card>
</template>

<script>
import DatePicker from './DatePicker.vue'
import {mapGetters, mapState} from 'vuex'

export default {
  name: 'PhotoFilterModal',
  components: {
    DatePicker
  },
  props: {
    photoFilters: {
      type: Object
    },
    retailerChoices: {
      type: Array,
      default: () => []
    },
    departmentChoices: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      selectedCategory: 0,
      selectedTags: [],
      selectedProjects: [],
      selectedJobs: [],
      tagSearchInput: null,
      responseIdSearchInput: null,
      fromDateVal: null,
      thruDateVal: null,
      onlyUntagged: true,
      includePartiallyTagged: true,
      includeInactive: false,
      respIdSearchFreeForm: null,
      rgidSearchFreeForm: null,
      question_id: null,
      selectedTaggerResponseIds: [],
      selectedCountry: null,
      countries: [
          {
              value: 'US',
              label: 'United States'
          },
          {
              value: 'CA',
              label: 'Canada'
          }
      ],
      selectedRetailer: null,
      selectedDepartment: null
    }
  },
  mounted() {
    this.selectedTags = this.photoFilters.tags
    this.selectedProjects = this.photoFilters.projects
    this.selectedJobs = this.photoFilters.jobs
    this.onlyUntagged = this.photoFilters.onlyUntagged
    this.includePartiallyTagged = this.photoFilters.includePartiallyTagged !== undefined
        ? this.photoFilters.includePartiallyTagged : true
    this.includeInactive = this.photoFilters.includeInactive === true

    if (this.photoFilters.fromDt != null) {
      this.fromDateVal = this.photoFilters.fromDt
    }

    if (this.photoFilters.thruDt != null) {
      this.thruDateVal = this.photoFilters.thruDt
    }

    if (this.photoFilters.respIdSearchFreeForm != null) {
      this.respIdSearchFreeForm = this.photoFilters.respIdSearchFreeForm
    }

    if (this.photoFilters.rgidSearchFreeForm != null) {
      this.rgidSearchFreeForm = this.photoFilters.rgidSearchFreeForm
    }

    if (this.photoFilters.question_id != null) {
      this.question_id = this.photoFilters.question_id
    }

    this.selectedCountry = this.photoFilters.country
    this.selectedRetailer = this.photoFilters.retailer
    this.selectedDepartment = this.photoFilters.department

    this.$emit('modal-mounted', true)
  },
  computed: {
    ...mapState({
      tags: state => state.tags.tags,
      categories: state => state.categories.categories,
      filteredTags: state => state.tags.filteredTags
    }),
    ...mapGetters(['getProjects', 'getJobs',]),
    allCats() {
      let cats = this.categories
      cats = cats.map(cat => ({id: cat.id, name: cat.name}))
      cats.push({id: 0, name: 'All'})
      return cats
    },
    defaultFromDt() {
      return this.fromDateVal;
    },
    defaultToDt() {
      return this.thruDateVal;
    },
    filteredTags() {
      let categId = parseInt(this.selectedCategory)
      if (categId > 0) {
        return this.tags.filter(tag => tag.category.id === categId)
      } else {
        return this.tags
      }
    }
  },
  methods: {
    removeTagFromSelected(tag) {
      let index = this.selectedTags.indexOf(tag)
      if (index >= 0) this.selectedTags.splice(index, 1)
    },
    tagFilter(item, queryText, itemText) {
      const cleanName = item.name.toLowerCase()
      const searchText = queryText.toLowerCase()
      return cleanName.indexOf(searchText) > -1
    },
    removeProjectFromSelected(prj) {
      let index = this.selectedProjects.indexOf(prj)
      if (index >= 0) this.selectedProjects.splice(index, 1)
    },
    projectFilter(item, queryText, itemText) {
      const cleanName = item.name.toLowerCase()

      const searchText = queryText.toLowerCase()
      return cleanName.indexOf(searchText) > -1 ||
          item.id.toString().indexOf(searchText) > -1
    },

    taggerResponseIdsFilter(item, queryText, itemText){
      const cleanName = item.id.toString().toLowerCase()
      const searchText = queryText.toString().toLowerCase()
      return cleanName.indexOf(searchText) > -1 ||
          item.id.toString().indexOf(searchText) > -1
    },
    removeJobFromSelected(job) {
      let index = this.selectedJobs.indexOf(job)
      if (index >= 0) this.selectedJobs.splice(index, 1)
    },
    jobFilter(item, queryText, itemText) {
      const cleanName = item.name.toLowerCase()

      const searchText = queryText.toLowerCase()
      return cleanName.indexOf(searchText) > -1 ||
          item.id.toString().indexOf(searchText) > -1
    },
    filterTagByCategory(e) {
      // var filteredTags = null;
      // let tagFilter = this.filters.find(f => f.label === 'Tags')
      if (e !== 0) {
        this.filteredTags = this.tags.filter(tag => tag.category.id === parseInt(e))
      } else {
        this.filteredTags = tagFilter.items
      }

      // tagFilter.items = filteredTags
    },
    cancelClicked() {
      this.$emit('dialog-clicked')
    },
    okClicked() {
      let selectedFilters = {
        fromDt: this.fromDateVal,
        thruDt: this.thruDateVal,
        onlyUntagged: this.onlyUntagged,
        includePartiallyTagged: this.includePartiallyTagged,
        includeInactive: this.includeInactive,
        country: this.selectedCountry,
        retailer: this.selectedRetailer,
        department: this.selectedDepartment
      }

      // Tags
      Object.assign(selectedFilters, {tags: this.selectedTags})
      // Projects
      Object.assign(selectedFilters, {projects: this.selectedProjects})
      // Jobs
      Object.assign(selectedFilters, {jobs: this.selectedJobs})
      //Mass Response ID Search
      Object.assign(selectedFilters, {respIdSearchFreeForm: this.respIdSearchFreeForm})
      // Response Group ID Search (store-visit ids)
      Object.assign(selectedFilters, {rgidSearchFreeForm: this.rgidSearchFreeForm})
      // Question ID
      Object.assign(selectedFilters, {question_id: this.question_id})

      this.$emit('apply-filters', selectedFilters)
      this.$emit('dialog-clicked')

    }

  }
}
</script>

<style>
</style>
